import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Button, Offcanvas } from 'react-bootstrap';
import Pagination from '../../ui/Pagination/Pagination';
import { useAuth } from '../../../firebase/AuthProvider';
import { PaginatedTaskActionsResponse, TaskAction, TaskActionFilterParams } from '../../../interfaces/participant/ParticipantTask';
import { getTaskActionsByParticipant } from '../../../services/participantServices/ParticipantDataService';
import { getTaskActionSubTypes } from '../../../services/taskServices/TaskActionService';
import ActionSubType from '../../../interfaces/task/taskActions/ActionSubType';
import './ParticipantActivityPage.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSort } from '@fortawesome/free-solid-svg-icons';
import filtericon from '../../../assets/images/filtericon.svg';
import Select from 'react-select';

interface OptionType {
    value: string;
    label: string;
}

const ParticipantActivityPage: React.FC = () => {
    const { currentUser, currentOrganization } = useAuth();
    const { id: participantId } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [activities, setActivities] = useState<PaginatedTaskActionsResponse | null>(null);
    const [subTypes, setSubTypes] = useState<ActionSubType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [showFilters, setShowFilters] = useState(false);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortBy, setSortBy] = useState('createDate');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [actionSubTypeId, setActionSubTypeId] = useState<number | undefined>();

    const fetchActivities = useCallback(async () => {
        if (!currentUser || !participantId) return;

        setLoading(true);
        try {
            const token = await currentUser.getIdToken();
            const numericParticipantId = parseInt(participantId, 10);

            if (isNaN(numericParticipantId)) {
                toast.error("Invalid participant ID.");
                setLoading(false);
                return;
            }

            const filterParams: TaskActionFilterParams = {
                pageNumber: page,
                pageSize: pageSize,
                sortBy: sortBy,
                sortOrder: sortOrder,
                actionSubTypeId: actionSubTypeId,
            };

            const response = await getTaskActionsByParticipant(numericParticipantId, token, filterParams);
            setActivities(response);

        } catch (error: any) {
            toast.error(error.message || "Failed to fetch activities.");
        } finally {
            setLoading(false);
        }
    }, [currentUser, participantId, page, pageSize, sortBy, sortOrder, actionSubTypeId]);

    const fetchSubTypes = useCallback(async () => {
        if (!currentUser) return;
        try {
            const token = await currentUser.getIdToken();
            const response = await getTaskActionSubTypes(token);
            if (response) {
                setSubTypes(response);
            }
        } catch (error: any) {
            toast.error(error.message || "Failed to fetch action subtypes.");
        }
    }, [currentUser]);

    useEffect(() => {
        fetchActivities();
        fetchSubTypes();
    }, [fetchActivities, fetchSubTypes]);

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const resetPageSize = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(1);
    };

    const handleSort = (column: string) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('desc');
        }
    };
    
    const clearFilters = () => {
        setActionSubTypeId(undefined);
        setSortBy('createDate');
        setSortOrder('desc');
    }

    const subTypeOptions: OptionType[] = subTypes.map(st => ({ value: st.id.toString(), label: st.actionSubTypeName.toUpperCase() }));

    console.log('Actions', activities)

    return (
        <div className="activity-page-container">
            <div className="common_card">
                <div className="top top_bar_flx">
                    <div className="top_bar_tittle">
                        <h2 className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faArrowLeft} className="me-2 cursor-pointer" onClick={() => navigate(-1)} />
                            Participant Activity
                        </h2>
                    </div>
                    <div className="top_right">
                        <Button style={{ minWidth: 'unset' }} variant="light" className="btn_sm" onClick={() => setShowFilters(true)}>
                            <img style={{ width: '20px' }} src={filtericon} alt="filtericon" />Filter
                        </Button>
                    </div>
                </div>

                <div className="activity-table-header">
                    <div className="header-cell" onClick={() => handleSort('author')}>
                        Author <FontAwesomeIcon icon={faSort} />
                    </div>
                    <div className="header-cell">Description</div>
                    <div className="header-cell" onClick={() => handleSort('actionType')}>
                        Type <FontAwesomeIcon icon={faSort} />
                    </div>
                    <div className="header-cell" onClick={() => handleSort('createDate')}>
                        Date <FontAwesomeIcon icon={faSort} />
                    </div>
                </div>

                {loading ? (
                    <div className="text-center p-4">Loading activities...</div>
                ) : !activities || activities.items.length === 0 ? (
                    <div className="text-center p-4">No activities found.</div>
                ) : (
                    <>
                        {activities.items.map((activity: TaskAction) => (
                            <div className="activity-table-row" key={activity.id}
                                onClick={() => navigate(`/task/${currentOrganization?.abbreviation}-${activity.taskId}`)}>
                                <div className="row-cell">
                                    {activity.author ? `${activity.author.firstName} ${activity.author.lastName}` : 'System'}
                                </div>
                                <div className="row-cell">{activity.description}</div>
                                <div className="row-cell">{activity.actionSubType?.actionSubTypeName || activity.actionType.name}</div>
                                <div className="row-cell">{moment(activity.createDate).format('MM/DD/YYYY h:mm A')}</div>
                            </div>
                        ))}
                        {activities.totalPages > 1 && (
                            <Pagination
                                currentPage={activities.pageNumber}
                                totalPages={activities.totalPages}
                                onPageChange={handlePageChange}
                                pageSize={activities.pageSize}
                                resetPageSize={(size: number) => resetPageSize(size)}
                            />
                        )}
                    </>
                )}
            </div>

            <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="end" className="my_offcanvas">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Filters</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <div className="custom_input_flled">
                        <label>Action Type</label>
                        <Select
                            options={subTypeOptions}
                            value={subTypeOptions.find(opt => opt.value === actionSubTypeId?.toString())}
                            onChange={(option) => setActionSubTypeId(option ? parseInt(option.value) : undefined)}
                            placeholder="Filter by Action Type"
                            isClearable
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                </Offcanvas.Body>
                <div className="offcanvas_form_btn_flx">
                    <Button variant="secondary" onClick={clearFilters}>Clear</Button>
                    <Button variant="primary" onClick={() => setShowFilters(false)}>Apply Filter</Button>
                </div>
            </Offcanvas>
        </div>
    );
};

export default ParticipantActivityPage;
